import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ListTodo, Users, CheckSquare, Send, Filter, Search,
  AlertTriangle, ChevronRight, Clock, Wrench
} from 'lucide-react';

const SEVERITY_COLORS = {
  blocker: 'bg-red-100 text-red-800', major: 'bg-orange-100 text-orange-800',
  minor: 'bg-yellow-100 text-yellow-800', observation: 'bg-slate-100 text-slate-600',
};

export default function TaskAllocation({ projectId, settings }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [showAssign, setShowAssign] = useState(false);
  const [filterTrade, setFilterTrade] = useState('');
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [assignForm, setAssignForm] = useState({ assigned_company: '', assigned_contact: '', due_date: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterTrade) params.trade_code = filterTrade;
      if (showUnassigned) params.unassigned_only = true;
      const res = await api.closeout.getTaskAllocation(projectId, params);
      setData(res.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId, filterTrade, showUnassigned]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (!data?.defects) return;
    if (selected.size === data.defects.length) setSelected(new Set());
    else setSelected(new Set(data.defects.map(d => d.id)));
  };

  const handleBulkAssign = async () => {
    if (!assignForm.assigned_company.trim()) { toast.error('Company name required'); return; }
    if (selected.size === 0) { toast.error('No items selected'); return; }
    try {
      const res = await api.closeout.bulkAssign(projectId, {
        defect_ids: [...selected],
        ...assignForm,
      });
      toast.success(`${res.data.count} items assigned to ${assignForm.assigned_company}`);
      setShowAssign(false);
      setSelected(new Set());
      setAssignForm({ assigned_company: '', assigned_contact: '', due_date: '', notes: '' });
      loadData();
    } catch { toast.error('Assignment failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const defects = data?.defects || [];
  const trades = [...new Set(defects.map(d => d.trade_code).filter(Boolean))];

  return (
    <div className="space-y-4" data-testid="task-allocation">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Task Allocation
          <span className="ml-2 text-sm font-normal text-slate-400">({data?.total || 0} items, {data?.unassigned || 0} unassigned)</span>
        </h2>
        <Button
          size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1"
          onClick={() => setShowAssign(true)}
          disabled={selected.size === 0}
          data-testid="bulk-assign-btn"
        >
          <Send className="h-4 w-4" /> Assign Selected ({selected.size})
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={filterTrade} onValueChange={v => setFilterTrade(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Trades" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trades</SelectItem>
            {trades.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showUnassigned} onChange={e => setShowUnassigned(e.target.checked)} className="rounded" />
          Unassigned only
        </label>
        <Button variant="outline" size="sm" onClick={toggleSelectAll} className="ml-auto">
          {selected.size === defects.length ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {/* Recent Batches */}
      {data?.recent_batches?.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <h4 className="text-xs font-bold uppercase text-slate-500 mb-2">Recent Assignments</h4>
            <div className="flex flex-wrap gap-2">
              {data.recent_batches.slice(0, 5).map(b => (
                <Badge key={b.id} variant="outline" className="text-xs">
                  {b.company}: {b.defect_count} items ({new Date(b.created_at).toLocaleDateString()})
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Defect List with Checkboxes */}
      {defects.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><ListTodo className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-lg font-medium text-slate-500">No items found</p></CardContent></Card>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={selected.size === defects.length && defects.length > 0} onChange={toggleSelectAll} className="rounded" /></th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Ref</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Description</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Location</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Trade</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Severity</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Assigned To</th>
                <th className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {defects.map(d => (
                <tr key={d.id} className={`border-b hover:bg-slate-50 ${selected.has(d.id) ? 'bg-blue-50' : ''}`} data-testid={`task-row-${d.id}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} className="rounded" /></td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{d.defect_no}</td>
                  <td className="px-3 py-2 max-w-[250px]"><p className="truncate">{d.description_short}</p></td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{d.location_text}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{d.trade_code || '-'}</Badge></td>
                  <td className="px-3 py-2"><Badge className={`text-[10px] ${SEVERITY_COLORS[d.severity]}`}>{d.severity}</Badge></td>
                  <td className="px-3 py-2 text-xs">{d.assigned_company || <span className="text-slate-300">-</span>}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{d.status?.replace(/_/g, ' ')}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Bulk Assign to Subcontractor</DialogTitle>
          <DialogDescription>Assign {selected.size} selected item{selected.size > 1 ? 's' : ''} to a subcontractor</DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Subcontractor Company *</Label>
              <Input value={assignForm.assigned_company} onChange={e => setAssignForm(f => ({...f, assigned_company: e.target.value}))} placeholder="e.g. ABC HVAC Contractors" data-testid="assign-company-input" />
            </div>
            <div>
              <Label className="text-xs">Contact Person</Label>
              <Input value={assignForm.assigned_contact} onChange={e => setAssignForm(f => ({...f, assigned_contact: e.target.value}))} placeholder="e.g. John Smith" />
            </div>
            <div>
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={assignForm.due_date} onChange={e => setAssignForm(f => ({...f, due_date: e.target.value}))} />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={assignForm.notes} onChange={e => setAssignForm(f => ({...f, notes: e.target.value}))} placeholder="Optional instruction notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleBulkAssign} data-testid="confirm-assign-btn">
              <Send className="h-4 w-4 mr-1" /> Assign {selected.size} Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
